import { Content, isFilled } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Link from "next/link";
import Image from "next/image";
import { Instagram } from "lucide-react";
import type { Feed } from "@behold/types";
import { __ } from "@/lib/i18n/functions";
import { appLocales, Locales } from "@/lib/i18n";
import { buttonVariants } from "@/components/atoms/buttons/Button";
import { cn } from "@/lib/utils";

export type InstagramFeedProps = SliceComponentProps<Content.InstagramFeedSlice> & {
  context?: { locale?: Locales };
};

const ONE_DAY_IN_SECONDS = 86400;

const fetchInstagramFeed = async () => {
  const feedId = process.env.BEHOLD_FEED_ID || "";
  if (!feedId) return;

  try {
    const response = await fetch(`https://feeds.behold.so/${feedId}`, {
      next: {
        revalidate: ONE_DAY_IN_SECONDS,
      },
    });

    if (!response.ok) {
      console.error(`Instagram feed fetch failed: ${response.status} ${response.statusText}`);
      return null;
    }

    const data: Feed = await response.json();

    return data.posts?.slice(0, 4) || null;
  } catch (error) {
    console.error("Error fetching Instagram feed:", error);
    return null;
  }
};

const InstagramFeed = async ({ slice, context }: InstagramFeedProps) => {
  const locale = context?.locale || appLocales.defaultLocale;
  const { title, description, instagram_tag } = slice.primary;
  const cleanTag = instagram_tag?.trim().replace(/^@/, "");
  const displayTag = `@${cleanTag}`;
  const instagramUrl = cleanTag ? `https://instagram.com/${cleanTag}` : "https://instagram.com";
  const posts = await fetchInstagramFeed();

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      aria-labelledby={__("Instagram feed", locale)}
      className="container mt-5 mb-10 flex flex-col items-center px-4 md:px-7.5"
    >
      <header className="mx-auto max-w-[500px] text-center">
        <h2
          id={__("Instagram feed", locale)}
          className="text-primary font-cabinet-grotesk pb-2.5 text-3xl font-medium md:text-4xl"
        >
          {title}
        </h2>
        {isFilled.richText(description) && (
          <div className="text-muted-foreground mx-auto text-base font-normal" style={{ maxWidth: "min(100%, 500px)" }}>
            <PrismicRichText field={description} />
          </div>
        )}
        <Link
          href={instagramUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(buttonVariants({ size: "md", variant: "secondary" }), "mt-5 bg-[#393939] text-white")}
        >
          <Instagram aria-hidden="true" />
          {displayTag}
        </Link>
      </header>

      {posts && posts.length > 0 && (
        <ul className="mt-[30px] grid grid-cols-2 gap-2.5 md:grid-cols-3 xl:grid-cols-4">
          {posts.map((post) => {
            if (post.mediaType === "VIDEO") return null;

            return (
              <li key={post.id}>
                <figure className="group relative overflow-hidden rounded-md">
                  <Image
                    src={post.sizes.medium.mediaUrl}
                    alt={post.prunedCaption || "Instagram post"}
                    width={post.sizes.medium.width}
                    height={post.sizes.medium.height}
                    className="aspect-square rounded-md object-cover transition-all duration-200 group-hover:scale-105"
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                    loading="lazy"
                  />
                  <Link href={post.permalink} target="_blank" rel="noopener noreferrer" className="absolute inset-0">
                    <figcaption className="sr-only">{post.caption}</figcaption>
                  </Link>
                </figure>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default InstagramFeed;
